// Instant hover labels.
//
// Almost every icon/button in the app carries a native `title=""` attribute for
// its hover label. The browser renders those with a fixed ~0.5–1.5s show delay
// that CSS can't touch, so labels feel laggy. This intercepts any element with a
// `title`, suppresses the native tooltip, and paints a custom chip near-instantly
// — no view changes required, works everywhere a `title` already exists.
//
// daisyUI tooltips (`.tooltip[data-tip]`) are left alone: they key off `data-tip`,
// not `title`, so they never match here.

const SHOW_DELAY = 60 // ms — quick enough to feel instant, long enough to avoid flicker while skimming a toolbar
const SELECTOR = "[title], [data-fd-title]"

let activeEl = null // element whose label is pending/showing
let stripped = false // did we move its `title` out of the way? (pointer only)
let showTimer = null
let tip = null

function ensureTip() {
  // FD-412: a Turbo morph refresh deletes our chip from <body> (it isn't in the server
  // HTML), leaving `tip` pointing at a detached node. Recreate it when it's not connected.
  if (tip && tip.isConnected) return tip
  tip = document.createElement("div")
  tip.className = "fd-tooltip"
  tip.setAttribute("role", "tooltip")
  tip.setAttribute("aria-hidden", "true")
  tip.setAttribute("popover", "manual")
  document.body.appendChild(tip)
  return tip
}

// FD-1353: Cornerstone's overlays (cs-popover, cs-dropdown, cs-dialog, cs-drawer) draw in the
// browser's top layer, above any z-index, so a chip in the page sat under them: the hints on
// the Filters fields never showed. The chip is a manual popover, which puts it in the top
// layer too. It is shown again each time, because the top layer stacks in the order things
// were shown, and an overlay may have opened since.
function raise(t) {
  if (!t.showPopover) return
  if (t.matches(":popover-open")) t.hidePopover()
  t.showPopover()
}

function labelFor(el) {
  return (el.getAttribute("title") ?? el.getAttribute("data-fd-title") ?? "").trim()
}

// strip: true for pointer hovers (we must remove `title` to kill the native
// tooltip). For keyboard focus the native tooltip never shows, so we leave
// `title` in place — assistive tech still reads it.
function open(el, { strip }) {
  if (activeEl === el) return
  close()

  const text = labelFor(el)
  if (!text) return

  activeEl = el
  if (strip && el.hasAttribute("title")) {
    el.setAttribute("data-fd-title", el.getAttribute("title"))
    el.removeAttribute("title")
    stripped = true
  }
  showTimer = window.setTimeout(() => render(el, text), SHOW_DELAY)
}

function close() {
  if (showTimer) {
    clearTimeout(showTimer)
    showTimer = null
  }
  if (activeEl && stripped && activeEl.hasAttribute("data-fd-title")) {
    activeEl.setAttribute("title", activeEl.getAttribute("data-fd-title"))
    activeEl.removeAttribute("data-fd-title")
  }
  activeEl = null
  stripped = false
  if (tip) tip.classList.remove("fd-tooltip-show")
}

function render(el, text) {
  if (!el.isConnected) return close()

  const t = ensureTip()
  t.textContent = text
  raise(t)

  // Measure (works while hidden: it is shown at opacity 0, and only opacity is toggled).
  const m = t.getBoundingClientRect()
  const r = el.getBoundingClientRect()
  const gap = 6
  const margin = 4

  // Prefer below the element; flip above when it would clip the bottom edge.
  let top = r.bottom + gap
  if (top + m.height > window.innerHeight - margin) {
    const above = r.top - gap - m.height
    if (above >= margin) top = above
  }
  top = Math.max(margin, top)

  let left = r.left + r.width / 2 - m.width / 2
  left = Math.max(margin, Math.min(left, window.innerWidth - m.width - margin))

  t.style.left = `${Math.round(left)}px`
  t.style.top = `${Math.round(top)}px`
  t.classList.add("fd-tooltip-show")
}

// --- wiring -----------------------------------------------------------------

document.addEventListener("pointerover", (e) => {
  if (e.pointerType === "touch") return // touch has no hover; let the tap happen
  const el = e.target.closest?.(SELECTOR)
  if (el) open(el, { strip: true })
  else close()
})

// Mouse left the document entirely (relatedTarget null) — drop any active label.
document.addEventListener("pointerout", (e) => {
  if (!e.relatedTarget && activeEl) close()
})

document.addEventListener("focusin", (e) => {
  const el = e.target.closest?.(SELECTOR)
  if (el) open(el, { strip: false })
})
document.addEventListener("focusout", () => close())

// Don't leave a label hanging once the user acts or the view moves.
document.addEventListener("pointerdown", () => close(), true)
document.addEventListener("scroll", () => close(), true)
document.addEventListener("keydown", (e) => {
  if (e.key === "Escape") close()
})

// Turbo: tear down before navigating/caching so a restored snapshot is clean
// (titles back in place, no orphaned chip).
document.addEventListener("turbo:before-cache", () => close())
document.addEventListener("turbo:before-visit", () => close())
document.addEventListener("turbo:before-render", () => close())
